/**
 * CSV 导出工具函数
 */

export interface SessionExportData {
  id: string;
  room_id: number;
  streamer_name: string;
  room_title: string;
  actual_start_time: string | null;
  end_time: string | null;
  duration_minutes: number;
  danmaku_count: number;
  danmaku_users: number;
  peak_watched_count: number;
  avg_watched_count: number;
  peak_like_count: number;
  peak_online_count: number;
  avg_online_count: number;
  screenshot_count: number;
  video_segment_count: number;
  is_replay: boolean | null;
  status: string;
}

/**
 * 生成CSV内容
 * @param data 场次数据列表
 * @returns CSV字符串
 */
export const generateSessionsCSV = (data: SessionExportData[]): string => {
  // CSV表头（中文）
  const headers = [
    '场次ID',
    '房间号',
    '主播名称',
    '直播标题',
    '开始时间',
    '结束时间',
    '时长(分钟)',
    '弹幕总数',
    '互动用户数',
    '峰值观看数',
    '平均观看数',
    '峰值点赞数',
    '峰值在线人数',
    '平均在线人数',
    '截图数',
    '视频片段数',
    '是否录播',
    '状态',
  ];

  // 转换数据行
  const rows = data.map((session) => [
    session.id,
    session.room_id,
    session.streamer_name,
    session.room_title,
    session.actual_start_time || '',
    session.end_time || '',
    session.duration_minutes,
    session.danmaku_count,
    session.danmaku_users,
    session.peak_watched_count,
    session.avg_watched_count,
    session.peak_like_count,
    session.peak_online_count,
    session.avg_online_count,
    session.screenshot_count,
    session.video_segment_count,
    session.is_replay ? '是' : '否',
    session.status === 'live' ? '直播中' : session.status === 'ended' ? '已结束' : '已计划',
  ]);

  // 组合CSV内容
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row.map((cell) => {
        // 处理包含逗号、引号、换行符的单元格
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',')
    ),
  ].join('\n');

  return csvContent;
};

/**
 * 下载CSV文件到浏览器
 * @param csvContent CSV内容
 * @param filename 文件名（不含扩展名）
 */
export const downloadCSV = (csvContent: string, filename: string) => {
  // 添加UTF-8 BOM以支持Excel正确显示中文
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // 创建下载链接
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // 释放URL对象
  URL.revokeObjectURL(url);
};

/**
 * 格式化时间范围描述
 */
export const formatTimeRangeText = (days: number): string => {
  if (days === 7) return '最近7天';
  if (days === 15) return '最近15天';
  if (days === 30) return '最近30天';
  return `最近${days}天`;
};

